import { supabase } from './client';
import { authStore } from '@stores/session';
import type { StrainRow } from './queries';
import { fetchStrainSymptoms } from './strainSymptoms';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type PortfolioSymptom = {
  id: string;
  name: string;
  status: 'pending' | 'approved';
};

export type PortfolioEntry = {
  id: string;
  strain_id: string;
  added_at: string;
  notes: string | null;
  symptoms: PortfolioSymptom[];
  strain: StrainRow;
};

/**
 * Return the patient's portfolio joined to the strain detail, ordered by
 * most recently added. Symptom tags now live in user_strain_symptoms
 * (migration 0049) keyed by (profile, strain) rather than on a portfolio
 * join, so we fetch the user's strain-symptom map once and merge it in by
 * strain_id.
 *
 * `symptoms` can include the user's own pending suggestions (visible to
 * them via the "read own pending" RLS policy); the client renders pending
 * rows visually distinct from approved.
 */
export async function fetchPortfolio(): Promise<PortfolioEntry[]> {
  const profile = authStore.profile();
  if (!profile) return [];

  const { data, error } = await db
    .from('user_portfolio')
    .select(
      'id, strain_id, added_at, notes, ' +
        'strain:strains(id, name, brand, genetics, category, slug, lineage)',
    )
    .eq('profile_id', profile.id)
    .order('added_at', { ascending: false });

  if (error) throw error;

  const symptomMap = await fetchStrainSymptoms();

  type Row = {
    id: string;
    strain_id: string;
    added_at: string;
    notes: string | null;
    strain: StrainRow;
  };

  return ((data ?? []) as Row[]).map((row) => ({
    id: row.id,
    strain_id: row.strain_id,
    added_at: row.added_at,
    notes: row.notes,
    strain: row.strain,
    symptoms: symptomMap.get(row.strain_id) ?? [],
  }));
}

export async function addToPortfolio(strainId: string, notes?: string): Promise<string> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { data, error } = await supabase
    .from('user_portfolio')
    .insert({
      profile_id: profile.id,
      strain_id: strainId,
      notes: notes ?? null,
    })
    .select('id')
    .single();
  if (error) {
    if (error.code === '23505') {
      // Unique violation — strain is already in this user's portfolio.
      // Return the existing entry's id so callers (e.g. PortfolioToggle's
      // optimistic mutate) don't have to issue a follow-up SELECT.
      const existing = await supabase
        .from('user_portfolio')
        .select('id')
        .eq('profile_id', profile.id)
        .eq('strain_id', strainId)
        .single();
      if (existing.error) throw existing.error;
      return (existing.data as { id: string }).id;
    }
    throw error;
  }
  return (data as { id: string }).id;
}

export async function removeFromPortfolio(entryId: string): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { error } = await supabase
    .from('user_portfolio')
    .delete()
    .eq('id', entryId)
    .eq('profile_id', profile.id);
  if (error) throw error;
}

export async function isInPortfolio(strainId: string): Promise<boolean> {
  const profile = authStore.profile();
  if (!profile) return false;
  const { data, error } = await supabase
    .from('user_portfolio')
    .select('id')
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId)
    .maybeSingle();
  if (error) throw error;
  return data != null;
}

/**
 * Bulk membership lookup for the listing pages — returns the set of
 * strain IDs that are currently in the user's portfolio. Used by
 * /strains and /stock to pre-seed the per-card "Add to my strains"
 * button without making one query per visible card.
 */
export async function fetchPortfolioStrainIds(): Promise<Set<string>> {
  const profile = authStore.profile();
  if (!profile) return new Set();
  const { data, error } = await supabase
    .from('user_portfolio')
    .select('strain_id')
    .eq('profile_id', profile.id);
  if (error) throw error;
  return new Set(((data ?? []) as { strain_id: string }[]).map((r) => r.strain_id));
}

/**
 * Remove a portfolio entry by strain id rather than entry id. The
 * listing pages don't have the entry id to hand (that lives on the
 * /my-strains query), so this is the natural shape for the quick-
 * action button there. Idempotent — deleting a row that isn't there
 * is a no-op rather than an error.
 */
export async function removeFromPortfolioByStrainId(strainId: string): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { error } = await supabase
    .from('user_portfolio')
    .delete()
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId);
  if (error) throw error;
}
